
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import {
  sendOfficeFollowUp,
  sendHygAsstResponse,
  updateInvoiceNotes,
  updateInvoice,
  nudgeJames,
  getPaymentIntegrities,
  claimPayment,
  confirmManualPayment,
  getPaginateItems,
  getAdminStripeAccountStatus,
  createAdminStripeRequestInformationLink
} from "@/api";
import type { AdminStripeAccountSummary } from "@/api";
import { getOfficeChargeFailureMessage } from "@/helpers/claimPaymentOutcome";
import {
  canConfirmManualTalentPayment,
  manualPaymentTargetIsCurrent,
  runBoundManualTalentPaymentAction,
  runBoundOfficeChargeAction
} from "@/helpers/paymentLifecycleAdminGuard";
import {
  beginStripeRequest,
  finishStripeRequest,
  invalidateStripeStatusRequest,
  invalidateStripeRequest,
  isCurrentStripeRequest,
  loadCurrentStripeStatus
} from "@/utils/stripeRequestOrdering";
import {
  invalidateInvoiceDetailRequest,
  loadCurrentInvoiceDetails
} from "@/utils/invoiceDetailOrdering";
import { constants } from "@/utils/constants";
import {
  format,
  differenceInCalendarDays,
  parseISO,
  isValid as isValidDate
} from "date-fns";
import ColumnManager from "@/components/shared/ColumnManager.vue";
import EntityIdLink from "@/components/shared/EntityIdLink";
import StripeAccountStatusPanel from "@/components/shared/StripeAccountStatusPanel.vue";
import { debounce } from "lodash";
import { formatInvoicePaymentMethod } from "@/helpers/invoicePaymentMethod";

interface VuexMappedState {
  tableContent: InvoiceRow[];
  tableHeaders: Array<{ text: string; value: string }>;
  tablename: string;
}

interface InvoiceRow {
  id: number;
  jobId: number;
  locationId: number;
  totalInvoiceAmt: number;
  hygienistMarkedPaid: number;
  dentistMarkedPaid: number;
  manualHygienistId?: number | null;
  manualDentistId?: number | null;
  sentOn?: string | null;
  createdOn?: string | null;
  paidOn?: string | null;
  notes?: string | null;
  ticketLinks?: string | null;
  unresolved?: string | null;
  resolved?: string | null;
  isCentralBooked?: number | null;
  [key: string]: any;
}

interface PaymentIntegrityRecord {
  paymentLifecycleId?: number | null;
  talentPaymentStatus?: string | null;
  id: number;
  jobId: number;
  locationId?: number;
  hygienistId?: number;
  manualHygienistId?: number;
  firstName?: string;
  lastName?: string;
  hygFirstName?: string;
  hygLastName?: string;
  practiceName?: string;
  locationEmail?: string;
  email?: string;
  userEmail?: string;
  totalInvoiceAmt?: number;
  paymentDue?: string;
  dueYet?: number;
  payNowUsed?: number;
  startDate?: string;
  stripeAccountId?: string;
  stripeCustomerId?: string;
  hygienistPhone?: string;
  locationPhone?: string;
  etransfer?: string;
  gapUnpaid?: number;
  status?: number;
  paymentTerms?: number;
  paymentMethod?: number;
  paysW2?: number;
  country?: number;
  [key: string]: any;
}

export default Vue.extend({
  name: "Invoices",
  components: {
    ColumnManager,
    EntityIdLink,
    StripeAccountStatusPanel
  },
  data() {
    return {
      tableLoading: true,
      refreshingInvoices: false,
      activeTab: 0,
      searchEmail: "",
      searchInvJobId: "",
      searchNames: "",
      searchHygLocId: "",
      showInvoiceId: true,
      showJobId: true,
      totalItems: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ["createdOn"],
        sortDesc: [true]
      } as any,
      selectedInvoice: null as InvoiceRow | null,
      selectedInvoiceDetails: null as PaymentIntegrityRecord | null,
      detailLoading: false,
      invoiceDetailRequestId: 0,
      stripeAccountStatus: null as AdminStripeAccountSummary | null,
      stripeStatusLoading: false,
      stripeStatusRequestId: 0,
      stripeStatusError: "",
      stripeRequestLoading: false,
      stripeRequestId: 0,
      stripeRequestLink: "",
      currentHeaders: [] as Array<{ text: string; value: string; align?: string }>,
      baseHeaders: [
        { text: "Invoice ID", value: "id", align: "center" },
        { text: "Job ID", value: "jobId", align: "center" },
        { text: "Location ID", value: "locationId", align: "center" },
        { text: "Corporation", value: "isCentralBooked", align: "center" },
        { text: "Due Status", value: "dueStatus", align: "center" },
        { text: "Payment Terms", value: "paymentTerms", align: "center" },
        { text: "Due Date", value: "paymentDue", align: "center" },
        { text: "Payment Method", value: "paymentMethod", align: "center" },
        { text: "Total Amount", value: "totalInvoiceAmt", align: "center" },
        { text: "Talent Paid", value: "hygienistMarkedPaid", align: "center" },
        { text: "Office Paid", value: "dentistMarkedPaid", align: "center" },
        { text: "Sent", value: "sent", align: "center" },
        { text: "Credit Paid", value: "creditPaid", align: "center" },
        { text: "Travel Allowance", value: "travelAllowance", align: "center" },
        { text: "Created On", value: "createdOn", align: "center" },
        { text: "Sent On", value: "sentOn", align: "center" },
        { text: "Office Phone", value: "officePhone", align: "left" },
        { text: "Notes", value: "notes", align: "left" }
      ],
      dialogs: {
        note: false,
        resolution: false,
        ticket: false,
        charge: false,
        manualPayment: false,
        paid: false,
        notesView: false
      },
      selectedNotes: "",
      noteText: "",
      resolutionType: "Office Mailed Cheque",
      resolutionNotes: "",
      ticketLinkValue: "",
      manualPaymentReference: "",
      manualPaymentInvoiceId: null as number | null,
      paidToggleRole: "" as "" | "dentist",
      paidToggleValue: 1,
      actionsLoading: {
        officeFollowUp: false,
        hygResponse: false,
        addNote: false,
        markResolved: false,
        addTicket: false,
        nudgeJames: false,
        chargeOffice: false,
        manualPayment: false,
        dentistPaid: false
      }
    };
  },
  computed: {
    ...mapState(["tableContent", "tableHeaders", "tablename"]),
    invoices(): InvoiceRow[] {
      return ((this as unknown as VuexMappedState).tableContent as InvoiceRow[]) || [];
    },
    canConfirmManualTalentPayment(): boolean {
      return canConfirmManualTalentPayment(this.selectedInvoiceDetails);
    },
    computedHeaders(): Array<{ text: string; value: string; align?: string }> {
      // Use currentHeaders if available (from ColumnManager), otherwise use baseHeaders
      const headers = this.currentHeaders.length > 0 ? this.currentHeaders : this.baseHeaders;
      
      return headers
        .filter(header => {
          if (!this.showInvoiceId && header.value === "id") {
            return false;
          }
          if (!this.showJobId && header.value === "jobId") {
            return false;
          }
          return true;
        })
        .map(header => ({
          ...header,
          align: header.align || "center"
        }));
    },
    filteredInvoices(): InvoiceRow[] {
      // Check if date field contains a valid timestamp (not null, empty, or placeholder)
      const hasValidDate = (value: any): boolean => {
        if (value === null || value === undefined) {
          return false;
        }
        if (typeof value !== "string") {
          return true;
        }
        const text = value.trim();
        if (!text || text === "-" || text === "null" || text === "undefined") {
          return false;
        }
        if (text === "0000-00-00 00:00:00" || text === "0000-00-00") {
          return false;
        }
        return true;
      };

      // Filter by tab: 0=All, 1=Unresolved, 2=Resolved
      const filtered = this.invoices
        .filter(invoice => {
          if (this.activeTab === 2) {
            return hasValidDate(invoice.resolved);
          }
          if (this.activeTab === 1) {
            return (
              hasValidDate(invoice.unresolved) && !hasValidDate(invoice.resolved)
            );
          }
          return true;
        })
        .map(invoice => {
          const formatted = { ...invoice };
          const customTemplateFields = [
            "totalInvoiceAmt",
            "hygienistMarkedPaid",
            "dentistMarkedPaid",
            "sent",
            "creditPaid",
            "createdOn",
            "sentOn",
            "id",
            "jobId",
            "locationId",
            "notes",
            "isCentralBooked"
          ];

          Object.keys(formatted).forEach(key => {
            const value = formatted[key];
            if (customTemplateFields.includes(key)) {
              return;
            }
            if (
              value === null ||
              value === undefined ||
              value === "" ||
              (typeof value === "string" && value.trim() === "")
            ) {
              formatted[key] = "-";
            }
          });
          return formatted;
        });

      return filtered;
    },
    hygienistName(): string {
      if (!this.selectedInvoiceDetails) {
        return "N/A";
      }
      const detail = this.selectedInvoiceDetails;
      const first = detail.firstName || detail.hygFirstName;
      const last = detail.lastName || detail.hygLastName;
      return [first, last].filter(Boolean).join(" ") || "N/A";
    },
    paymentDueStatement(): string {
      if (
        !this.selectedInvoiceDetails ||
        !this.selectedInvoiceDetails.paymentDue
      ) {
        return "No due date";
      }
      const info = this.getDueInfo(this.selectedInvoiceDetails.paymentDue);
      if (!info.label) {
        return "No due date";
      }
      return `${info.label} (${info.daysText})`;
    },
    paidToggleRoleLabel(): string {
      return "dental office";
    },
    paidToggleLoading(): boolean {
      return this.actionsLoading.dentistPaid;
    },
    resolutionOptions(): string[] {
      return [
        "Office Mailed Cheque",
        "Office did E-Transfer",
        "Talent Indicated Receipt",
        "Other"
      ];
    }
  },
  watch: {
    tableOptions: {
      handler(newVal, oldVal) {
        if (oldVal && oldVal.page !== undefined) {
          if (
            newVal.page !== oldVal.page ||
            newVal.itemsPerPage !== oldVal.itemsPerPage
          ) {
            this.refreshInvoices();
          }
        }
      },
      deep: true
    },
    searchInvJobId() {
      this.debouncedRefresh();
    },
    searchEmail() {
      this.debouncedRefresh();
    },
    searchNames() {
      this.debouncedRefresh();
    },
    searchHygLocId() {
      this.debouncedRefresh();
    }
  },
  async created() {
    this.debouncedRefresh = debounce(() => {
      this.tableOptions.page = 1; // Reset to first page when searching
      this.refreshInvoices();
    }, 400);

    if ((this as unknown as VuexMappedState).tablename !== "Invoice") {
      this.$store.commit("modify", { property: "tablename", value: "Invoice" });
    }
    // T2-6225: when navigated to via an EntityIdLink (?invoiceId=N), seed the
    // Invoice # search so the table filters straight to that invoice.
    const linkedInvoiceId = (this as any).$route?.query?.invoiceId;
    if (linkedInvoiceId) {
      (this as any).searchInvJobId = String(linkedInvoiceId);
    }
    await this.refreshInvoices();
  },
  methods: {
    ...mapMutations(["modify", "callSnackbar"]),
    debouncedRefresh: (() => {}) as () => void, // Placeholder, initialized in created
    async refreshInvoices() {
      try {
        this.refreshingInvoices = true;
        this.tableLoading = true;

        const searchFilters: Array<{ search: string; columns: string[] }> = [];

        if (this.searchInvJobId) {
          searchFilters.push({
            search: this.searchInvJobId,
            columns: ["i.id", "i.jobId"]
          });
        }
        if (this.searchEmail) {
          searchFilters.push({
            search: this.searchEmail,
            columns: ["l.email", "u.email"]
          });
        }
        if (this.searchNames) {
          searchFilters.push({
            search: this.searchNames,
            columns: ["l.practiceName", "hygienistFullName"]
          });
        }
        if (this.searchHygLocId) {
          searchFilters.push({
            search: this.searchHygLocId,
            columns: ["j.hygienistId", "j.locationId"]
          });
        }

        const response = await getPaginateItems({
          tableName: "Invoice",
          limit: this.tableOptions.itemsPerPage,
          page: this.tableOptions.page,
          columnNames: constants.enabledColumns.invoiceTable,
          ...(searchFilters.length > 0 && { searchFilters })
        });

        this.$store.commit("modify", { property: "tableContent", value: response });

        if (response.length < this.tableOptions.itemsPerPage) {
          this.totalItems = (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage + response.length;
        } else if (this.totalItems === 0 || this.tableOptions.page === 1) {
          this.totalItems = response.length * 10;
        }
      } catch (error) {
        console.error("Error refreshing invoices:", error);
        this.callSnackbar({
          visible: true,
          text: "Failed to load invoices",
          color: "red darken-2"
        });
      } finally {
        this.tableLoading = false;
        this.refreshingInvoices = false;
      }
    },
    rowClass(item: InvoiceRow) {
      if (this.selectedInvoice && item.id === this.selectedInvoice.id) {
        return "selected-row";
      }
      return "";
    },
    formatCurrency(value?: number | null): string {
      if (!value && value !== 0) {
        return "$0.00";
      }
      return `$${Number(value).toFixed(2)}`;
    },
    formatDate(value?: string | null): string {
      if (!value || value === null || value === undefined || value === "") {
        return "-";
      }
      const parsed = parseISO(value.toString());
      if (!isValidDate(parsed)) {
        return "-";
      }
      return format(parsed, "MMM d, yyyy HH:mm");
    },
    formatDueDate(value?: string | null): string {
      if (!value) {
        return "N/A";
      }
      try {
        const parsed = parseISO(value.toString());
        if (!isValidDate(parsed)) {
          return value;
        }
        return format(parsed, "MMM d, yyyy");
      } catch (error) {
        return value || "N/A";
      }
    },
    getCorporationLabel(value: number | string | null | undefined): string {
      // Convert string to number if needed
      let numValue: number | null = null;
      if (typeof value === "string") {
        const parsed = parseInt(value, 10);
        numValue = isNaN(parsed) ? null : parsed;
      } else if (typeof value === "number") {
        numValue = value;
      }

      if (numValue === null || numValue === 0) {
        return "Regular Office";
      }
      if (numValue === 1) {
        return "DentalCorp";
      }
      if (numValue === 2) {
        return "123D";
      }
      return "Regular Office";
    },
    getCorporationChipColor(value: number | string | null | undefined): string {
      // Convert string to number if needed
      let numValue: number | null = null;
      if (typeof value === "string") {
        const parsed = parseInt(value, 10);
        numValue = isNaN(parsed) ? null : parsed;
      } else if (typeof value === "number") {
        numValue = value;
      }

      if (numValue === 1) {
        return "#6B5B95"; // Purple for DentalCorp
      }
      if (numValue === 2) {
        return "#FF6F61"; // Coral/Red for 123D
      }
      return "#88B04B"; // Green for Regular Office
    },
    formatPaymentMethod: formatInvoicePaymentMethod,
    handleRowClick(invoice: InvoiceRow) {
      this.resetStripeRequestState();
      invalidateStripeStatusRequest(this);
      this.manualPaymentReference = "";
      this.manualPaymentInvoiceId = null;
      this.selectedInvoice = invoice;
      this.ticketLinkValue = invoice.ticketLinks || "";
      this.noteText = "";
      this.resolutionNotes = "";
      this.selectedInvoiceDetails = null;
      this.stripeAccountStatus = null;
      this.stripeStatusLoading = false;
      this.stripeStatusError = "";
      this.stripeRequestLink = "";
      this.$store.commit("modify", {
        property: "selectedInvoiceId",
        value: invoice.id
      });
      this.loadInvoiceDetails(invoice.id);
    },
    async loadInvoiceDetails(invoiceId: number) {
      await loadCurrentInvoiceDetails<PaymentIntegrityRecord | null>({
        context: this,
        invoiceId,
        selectedInvoiceId: () => this.selectedInvoice?.id,
        fetchDetails: async () => {
          const response = await getPaymentIntegrities({
            column: "i.id",
            value: invoiceId.toString(),
            limit: 1
          } as any);
          return response && response[0] ? response[0] : null;
        },
        applyDetails: details => {
          this.selectedInvoiceDetails = details;
        },
        loadStripeStatus: () => this.loadSelectedInvoiceStripeStatus(),
        onError: error => this.showError("Unable to load invoice context", error)
      });
    },
    selectedInvoiceTalentId(): number | null {
      const rawId =
        this.selectedInvoiceDetails?.hygienistId ||
        this.selectedInvoiceDetails?.manualHygienistId ||
        this.selectedInvoice?.manualHygienistId;
      const numericId = Number(rawId);
      return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
    },
    async loadSelectedInvoiceStripeStatus() {
      const hygienistId = this.selectedInvoiceTalentId();
      if (!hygienistId) {
        invalidateStripeStatusRequest(this);
        this.stripeAccountStatus = null;
        this.stripeStatusError = "No Talent is associated with this invoice.";
        this.stripeStatusLoading = false;
        return;
      }
      const invoiceId = this.selectedInvoice?.id;
      this.stripeStatusError = "";
      this.stripeRequestLink = "";
      await loadCurrentStripeStatus({
        context: this,
        entityId: Number(invoiceId),
        selectedEntityId: () => this.selectedInvoice?.id,
        fetchStatus: () => getAdminStripeAccountStatus(hygienistId),
        applyStatus: status => {
          this.stripeAccountStatus = status;
        },
        onError: error => {
          this.stripeAccountStatus = null;
          this.stripeStatusError =
            error instanceof Error ? error.message : "Unable to load Stripe status";
        }
      });
    },
    resetStripeRequestState() {
      invalidateStripeRequest(this);
      this.stripeRequestLink = "";
    },
    async requestStripeInformationLink() {
      const hygienistId = this.selectedInvoiceTalentId();
      if (!hygienistId) return;
      const invoiceId = this.selectedInvoice?.id;
      const requestId = beginStripeRequest(this);
      this.stripeRequestLink = "";
      try {
        const result = await createAdminStripeRequestInformationLink(hygienistId);
        if (
          !isCurrentStripeRequest(this, requestId) ||
          this.selectedInvoice?.id !== invoiceId
        ) return;
        this.stripeRequestLink = result.url;
        this.showSuccess("Fresh Stripe Request Information link generated");
      } catch (error) {
        if (
          !isCurrentStripeRequest(this, requestId) ||
          this.selectedInvoice?.id !== invoiceId
        ) return;
        this.showError(
          error instanceof Error ? error.message : "Unable to generate Stripe link"
        );
      } finally {
        finishStripeRequest(this, requestId);
      }
    },
    clearSelection() {
      invalidateInvoiceDetailRequest(this);
      this.resetStripeRequestState();
      invalidateStripeStatusRequest(this);
      this.manualPaymentReference = "";
      this.manualPaymentInvoiceId = null;
      this.selectedInvoice = null;
      this.selectedInvoiceDetails = null;
      this.stripeAccountStatus = null;
      this.stripeStatusLoading = false;
      this.stripeStatusError = "";
      this.stripeRequestLink = "";
      this.$store.commit("modify", {
        property: "selectedInvoiceId",
        value: null
      });
    },
    showSuccess(message: string) {
      this.callSnackbar({
        visible: true,
        text: message,
        color: "green darken-2"
      });
    },
    showError(message: string, error?: unknown) {
      /* eslint-disable no-console */
      if (process.env.NODE_ENV !== "production") {
        console.error(message, error);
      }
      /* eslint-enable no-console */
      this.callSnackbar({
        visible: true,
        text: message,
        color: "red darken-2"
      });
    },
    async appendNote(note: string, boundInvoiceId?: number) {
      const invoiceId = boundInvoiceId || this.selectedInvoice?.id;
      if (!invoiceId) {
        return;
      }
      await updateInvoiceNotes({
        invoiceID: invoiceId,
        note
      });

      if (this.selectedInvoice?.id !== invoiceId) {
        return;
      }

      // Update local state: backend prepends timestamp and appends pipe
      const timestamp = format(new Date(), "yyyy-MM-dd HH:mm:ss");
      const noteWithTimestamp = `${timestamp} ${note} |`;
      const currentNotes = this.selectedInvoice.notes || "";
      const separator = currentNotes ? "\n" : "";
      const newNotes = `${currentNotes}${separator}${noteWithTimestamp}`;
      this.updateLocalInvoiceField("notes", newNotes);
    },
    openDialog(
      name: "note" | "resolution" | "ticket" | "charge" | "manualPayment"
    ) {
      if (name === "charge") {
        this.dialogs.charge = true;
        return;
      }
      if (name === "manualPayment") {
        this.manualPaymentReference = "";
        this.manualPaymentInvoiceId = this.selectedInvoice?.id || null;
      }
      this.dialogs[name] = true;
    },
    closeDialog(
      name:
        | "note"
        | "resolution"
        | "ticket"
        | "charge"
        | "paid"
        | "notesView"
        | "manualPayment"
    ) {
      this.dialogs[name] = false;
      if (name === "manualPayment") {
        this.manualPaymentReference = "";
        this.manualPaymentInvoiceId = null;
      }
    },
    truncateNotes(notes: string | null | undefined): string {
      // Check for null, undefined, or empty values
      if (!notes) {
        return "-";
      }

      // Convert to string and trim
      const notesStr = String(notes).trim();

      // Check for empty string or dash placeholder
      if (
        notesStr === "" ||
        notesStr === "-" ||
        notesStr.toLowerCase() === "n/a"
      ) {
        return "-";
      }

      // Return the full notes - CSS will handle 2-line truncation
      return notesStr;
    },
    shouldShowNotesButton(notes: string | null | undefined): boolean {
      // Check for null, undefined, or empty values
      if (!notes) {
        return false;
      }

      // Convert to string and trim
      const notesStr = String(notes).trim();

      // Check for empty string or dash placeholder
      if (
        notesStr === "" ||
        notesStr === "-" ||
        notesStr.toLowerCase() === "n/a"
      ) {
        return false;
      }

      // Split on actual line breaks to count real lines (filter out empty lines)
      const lines = notesStr
        .split(/\n|\r\n|\r/)
        .filter(line => line.trim().length > 0);

      // Rule 1: If there are more than 2 actual lines (after line breaks), show button
      if (lines.length > 2) {
        return true;
      }

      // Rule 2: Count all line break characters (including empty lines)
      const lineBreakCount = (notesStr.match(/\n|\r\n|\r/g) || []).length;
      if (lineBreakCount >= 2) {
        return true;
      }

      // Rule 3: Very conservative check - if text length > 100 characters, show button
      // This ensures we always show button when content might be truncated
      // 100 chars is conservative for a 300px wide column (~65-70 chars per line)
      if (notesStr.length > 100) {
        return true;
      }

      // Rule 4: If there's any line break and text is more than 60 chars, show button
      // A line break + substantial content likely means multiple display lines
      if (lineBreakCount >= 1 && notesStr.length > 60) {
        return true;
      }

      // Rule 5: If we have exactly 2 lines, check if combined length would wrap
      if (lines.length === 2) {
        const totalLength = lines.reduce((sum, line) => sum + line.length, 0);
        // If combined length is more than ~80 chars, likely to exceed 2 display lines
        if (totalLength > 80) {
          return true;
        }
        // If either individual line is longer than 60 chars, likely to wrap
        if (lines.some(line => line.length > 60)) {
          return true;
        }
      }

      // Rule 6: Estimate display lines for single line text that might wrap
      // Very conservative: assume ~60 chars per display line
      if (lines.length === 1) {
        const estimatedDisplayLines = Math.ceil(notesStr.length / 60);
        if (estimatedDisplayLines > 2) {
          return true;
        }
      }

      // Default: don't show button for very short text that definitely fits in 2 lines
      return false;
    },
    openNotesDialog(item: InvoiceRow) {
      this.selectedNotes = item.notes || "";
      this.dialogs.notesView = true;
    },
    async handleSendOfficeFollowup() {
      if (!this.selectedInvoiceDetails || !this.selectedInvoice) {
        return;
      }
      this.actionsLoading.officeFollowUp = true;
      try {
        const detail = this.selectedInvoiceDetails;
        await sendOfficeFollowUp({
          id: detail.id,
          jobId: detail.jobId,
          totalInvoiceAmt:
            detail.totalInvoiceAmt || this.selectedInvoice.totalInvoiceAmt,
          firstName: detail.firstName || detail.hygFirstName || "",
          lastName: detail.lastName || detail.hygLastName || "",
          startDate: detail.startDate || "",
          practiceName: detail.practiceName || "",
          locationEmail: detail.locationEmail || "",
          email: detail.email || detail.userEmail || "",
          paymentDue: detail.paymentDue || "",
          dueYet: detail.dueYet || 0,
          payNowUsed: detail.payNowUsed || 0,
          hygienistId: detail.hygienistId,
          locationId: detail.locationId
        });
        
        // Update local notes immediately
        const timestamp = format(new Date(), "yyyy-MM-dd HH:mm:ss");
        const note = `${timestamp} office follow-up e-mail sent |`;
        const currentNotes = this.selectedInvoice.notes || "";
        const separator = currentNotes ? "\n" : "";
        const newNotes = `${currentNotes}${separator}${note}`;
        this.updateLocalInvoiceField("notes", newNotes);

        await updateInvoice({
          invoiceId: this.selectedInvoice.id,
          unresolved: timestamp
        });
        this.showSuccess("Office follow-up sent");
      } catch (error) {
        this.showError("Unable to send office follow-up", error);
      } finally {
        this.actionsLoading.officeFollowUp = false;
      }
    },
    getDueInfo(paymentDue?: string) {
      if (!paymentDue) {
        return { label: "", daysText: "" };
      }
      const dueDate = parseISO(paymentDue);
      if (!isValidDate(dueDate)) {
        return { label: "", daysText: "" };
      }
      const diff = differenceInCalendarDays(dueDate, new Date());
      if (diff === 0) {
        return { label: "Due today", daysText: "0 days" };
      }
      if (diff > 0) {
        return { label: "Not due", daysText: `${diff} day(s)` };
      }
      return { label: "Overdue", daysText: `${Math.abs(diff)} day(s)` };
    },
    getInvoiceDueStatus(invoice: InvoiceRow): {
      isPastDue: boolean;
      label: string;
      daysText: string;
      color: string;
    } {
      // If office already paid, show as paid (1 = Pay Now, 2 = Already Paid outside TempStars)
      if (invoice.dentistMarkedPaid >= 1) {
        return {
          isPastDue: false,
          label: "Paid",
          daysText: "",
          color: "#13CA82"
        };
      }

      // Use backend-computed paymentDue (startDate + paymentTerms or sentOn + paymentTerms)
      if (!invoice.paymentDue) {
        return { isPastDue: false, label: "", daysText: "", color: "grey" };
      }

      try {
        const dueDate = parseISO(invoice.paymentDue.toString());
        if (!isValidDate(dueDate)) {
          return { isPastDue: false, label: "", daysText: "", color: "grey" };
        }

        const diff = differenceInCalendarDays(dueDate, new Date());

        if (diff < 0) {
          // Overdue
          const daysOverdue = Math.abs(diff);
          return {
            isPastDue: true,
            label: "Overdue",
            daysText: `${daysOverdue} day${daysOverdue !== 1 ? 's' : ''}`,
            color: "#FB7672"
          };
        } else if (diff === 0) {
          return {
            isPastDue: false,
            label: "Due",
            daysText: "today",
            color: "#FFA726"
          };
        } else {
          // Not due yet
          return {
            isPastDue: false,
            label: "Not Due",
            daysText: `${diff} day${diff !== 1 ? 's' : ''}`,
            color: "#13CA82"
          };
        }
      } catch {
        return { isPastDue: false, label: "", daysText: "", color: "grey" };
      }
    },
    async handleSendHygResponse() {
      if (!this.selectedInvoiceDetails || !this.selectedInvoice) {
        return;
      }
      this.actionsLoading.hygResponse = true;
      try {
        const detail = this.selectedInvoiceDetails;
        const dueInfo = this.getDueInfo(detail.paymentDue);
        const isOverdue = dueInfo.label === "Overdue";
        const mailTheme = isOverdue
          ? "Hyg Payment Response Overdue"
          : "Hyg Payment Response Not Due";
        const noteDue = isOverdue ? "overdue" : "not due";

        await Promise.all([
          sendHygAsstResponse({
            mailTheme,
            email: detail.email || detail.userEmail || "",
            invoiceID: detail.id,
            firstName: detail.firstName || detail.hygFirstName,
            lastName: detail.lastName || detail.hygLastName,
            shiftDate: detail.startDate,
            amount:
              detail.totalInvoiceAmt || this.selectedInvoice.totalInvoiceAmt,
            paymentDue: detail.paymentDue
          }),
          this.appendNote(`${noteDue} follow-up e-mail sent`),
          updateInvoice({
            invoiceId: this.selectedInvoice.id,
            unresolved: format(new Date(), "yyyy-MM-dd HH:mm:ss")
          })
        ]);

        this.showSuccess("Talent follow-up sent");
      } catch (error) {
        this.showError("Unable to send talent response", error);
      } finally {
        this.actionsLoading.hygResponse = false;
      }
    },
    async submitNote() {
      if (!this.noteText || !this.selectedInvoice) {
        return;
      }
      this.actionsLoading.addNote = true;
      try {
        await this.appendNote(this.noteText.trim());
        this.showSuccess("Note added to invoice");
        this.noteText = "";
        this.closeDialog("note");
      } catch (error) {
        this.showError("Unable to add note", error);
      } finally {
        this.actionsLoading.addNote = false;
      }
    },
    async submitResolution() {
      if (!this.selectedInvoice) {
        return;
      }
      this.actionsLoading.markResolved = true;
      try {
        const timestamp = format(new Date(), "yyyy-MM-dd HH:mm:ss");
        await Promise.all([
          updateInvoice({
            invoiceId: this.selectedInvoice.id,
            resolved: timestamp
          }),
          this.appendNote(
            `resolved by ${this.resolutionType}${
              this.resolutionNotes ? ` - ${this.resolutionNotes}` : ""
            }`
          )
        ]);
        this.updateLocalInvoiceField("resolved", timestamp);
        this.showSuccess("Invoice marked as resolved");
        this.closeDialog("resolution");
      } catch (error) {
        this.showError("Unable to resolve invoice", error);
      } finally {
        this.actionsLoading.markResolved = false;
      }
    },
    async submitTicketLink() {
      if (!this.selectedInvoice) {
        return;
      }
      this.actionsLoading.addTicket = true;
      try {
        await updateInvoice({
          invoiceId: this.selectedInvoice.id,
          ticketLinks: this.ticketLinkValue
        });
        this.updateLocalInvoiceField("ticketLinks", this.ticketLinkValue);
        this.showSuccess("Ticket link updated");
        this.closeDialog("ticket");
      } catch (error) {
        this.showError("Unable to save ticket link", error);
      } finally {
        this.actionsLoading.addTicket = false;
      }
    },
    async handleNudgeJames() {
      if (!this.selectedInvoice) {
        return;
      }
      this.actionsLoading.nudgeJames = true;
      try {
        await nudgeJames(this.selectedInvoice.id);
        this.showSuccess("James has been nudged");
      } catch (error) {
        this.showError("Unable to nudge James", error);
      } finally {
        this.actionsLoading.nudgeJames = false;
      }
    },
    triggerPaidToggle(role: "dentist") {
      if (!this.selectedInvoice) {
        return;
      }
      this.paidToggleRole = role;
      const field = "dentistMarkedPaid";
      const currentValue = (this.selectedInvoice as any)[field] || 0;
      this.paidToggleValue = currentValue >= 1 ? 0 : 1;
      this.dialogs.paid = true;
    },
    async submitPaidToggle() {
      if (!this.selectedInvoice || !this.paidToggleRole) {
        return;
      }
      this.dialogs.paid = false;
      this.actionsLoading.dentistPaid = true;
      try {
        const field = "dentistMarkedPaid";
        await updateInvoice({
          invoiceId: this.selectedInvoice.id,
          [field]: this.paidToggleValue
        } as any);
        this.updateLocalInvoiceField(field, this.paidToggleValue);
        this.showSuccess(
          "Office paid status updated"
        );
      } catch (error) {
        this.showError("Unable to update paid status", error);
      } finally {
        this.actionsLoading.dentistPaid = false;
        this.paidToggleRole = "";
      }
    },
    async submitChargeOffice() {
      if (!this.selectedInvoice) {
        return;
      }
      const invoiceId = this.selectedInvoice.id;
      this.actionsLoading.chargeOffice = true;
      try {
        const resolved = format(new Date(), "yyyy-MM-dd HH:mm:ss");
        const outcome = await runBoundOfficeChargeAction(invoiceId, resolved, {
          claimPayment,
          writeNote: (targetInvoiceId, note) =>
            this.appendNote(note, targetInvoiceId),
          updateInvoice,
          nudgeJames
        });
        if (this.selectedInvoice?.id === invoiceId) {
          this.updateLocalInvoiceField("dentistMarkedPaid", 1);
          if (outcome.shouldResolve) {
            this.updateLocalInvoiceField("resolved", resolved);
          }
        }
        if (outcome.requiresAction) {
          this.showError(outcome.message);
        } else {
          this.showSuccess(outcome.message);
        }
      } catch (error) {
        const warning = getOfficeChargeFailureMessage(error);
        try {
          await this.appendNote(warning, invoiceId);
        } catch (_) {
          // A bookkeeping failure must not hide the uncertain-payment warning.
        }
        this.showError(warning);
      } finally {
        this.actionsLoading.chargeOffice = false;
        if (this.selectedInvoice?.id === invoiceId) {
          this.closeDialog("charge");
        }
      }
    },
    async submitManualPayment() {
      if (
        this.actionsLoading.manualPayment ||
        !this.selectedInvoice ||
        !canConfirmManualTalentPayment(this.selectedInvoiceDetails) ||
        !manualPaymentTargetIsCurrent(
          this.selectedInvoice.id,
          this.manualPaymentInvoiceId,
          this.manualPaymentReference
        )
      ) {
        return;
      }
      this.actionsLoading.manualPayment = true;
      const reference = this.manualPaymentReference.trim();
      const invoiceId = Number(this.manualPaymentInvoiceId);
      try {
        const resolved = format(new Date(), "yyyy-MM-dd HH:mm:ss");
        const outcome = await runBoundManualTalentPaymentAction(invoiceId, reference, resolved, {
          confirmManualPayment,
          writeNote: (targetInvoiceId, note) =>
            updateInvoiceNotes({ invoiceID: targetInvoiceId, note }),
          updateInvoice
        });
        if (this.selectedInvoice?.id === invoiceId) {
          if (outcome.confirmed) {
            this.updateLocalInvoiceField("hygienistMarkedPaid", 1);
            this.updateLocalInvoiceField("dentistMarkedPaid", 1);
          }
          if (outcome.shouldResolve) {
            this.updateLocalInvoiceField("resolved", resolved);
          }
          if (outcome.requiresAction) {
            this.showError(outcome.message);
          } else {
            this.showSuccess(outcome.message);
          }
          this.closeDialog("manualPayment");
        }
      } catch (error) {
        if (this.selectedInvoice?.id === invoiceId) {
          this.showError(
            "Manual Talent payment outcome could not be confirmed. Do not pay again or send a replacement payment; refresh and contact Support."
          );
          this.closeDialog("manualPayment");
        }
      } finally {
        this.actionsLoading.manualPayment = false;
      }
    },
    updateLocalInvoiceField(field: string, value: any) {
      if (!this.selectedInvoice) {
        return;
      }
      this.selectedInvoice = { ...this.selectedInvoice, [field]: value };
      const updated = this.invoices.map(invoice =>
        invoice.id === this.selectedInvoice!.id
          ? { ...invoice, [field]: value }
          : invoice
      );
      this.modify({ property: "tableContent", value: updated });
    },
    updateHeaders(headers: Array<{ text: string; value: string }>) {
      this.currentHeaders = headers;
    },
    handleSnackbar(options: { text: string; color: string }) {
      this.callSnackbar({
        visible: true,
        text: options.text,
        color: options.color
      });
    },
    async updatePaidStatus(
      item: InvoiceRow,
      field: "dentistMarkedPaid",
      value: number
    ) {
      try {
        await updateInvoice({
          invoiceId: item.id,
          [field]: value
        } as any);

        // Update local state
        const updated = this.invoices.map(invoice =>
          invoice.id === item.id ? { ...invoice, [field]: value } : invoice
        );
        this.modify({ property: "tableContent", value: updated });

        // Update selected invoice if this is the one
        if (this.selectedInvoice && this.selectedInvoice.id === item.id) {
          this.selectedInvoice = { ...this.selectedInvoice, [field]: value };
        }

        this.showSuccess(
          "Office paid status updated"
        );
      } catch (error) {
        this.showError("Unable to update paid status", error);
        // Revert the value on error
        item[field] = value === 1 ? 0 : 1;
      }
    }
  }
});
